import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { DataCard } from '@/components/DataCard/DataCard'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import { Page } from '@progress/kendo-react-dropdowns'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { UploadFileInfo } from '@progress/kendo-react-upload'
import { CreatePromoMaterial } from 'api/ClientArea/CreatePromoMaterials'
import { PreviewPromoMaterial } from 'api/ClientArea/PreviewPromoMaterials'
import { PromoMaterialDto } from 'api/ClientArea/PromoMaterialDto'
import { NameDto } from 'api/Common/Types'
import AddButton from 'components/AddButton/AddButton'
import { Pager } from 'components/components/Pager'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Loading } from 'components/Loading'
import MoreButton from 'components/MoreButton/MoreButton'
import { errorToast, successToast, ToastContext } from 'components/Toast/Toast'
import { calculatePageIndex, calculateSkipIndex } from 'features/DataGrid/helpers'
import { pageableSettings } from 'store/grid/grid.slice'
import { ResponseError, useApiClient } from 'utils/api/ApiClient'
import { ClientAreaApiClient } from 'utils/api/clientAreaApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import ClientAreaAuditLogModal from '../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import type { GridColumn } from './PromoMaterialColumn'
import { promoMaterialColumn } from './PromoMaterialColumn'
import { PromoMaterialEditForm } from './PromoMaterialEditForm'
import { UpdatePromoMaterialValues } from './PromoMaterialForm'
import { PromoMaterialSaveForm } from './PromoMaterialSaveForm'
import { RelatedPromoMaterialUploadTable } from './RelatedPromoMaterialUploadTable'

const useRelatedPromoMaterialsFilter = () => {
  const { referralMaterialId } = useReferralMaterialRoute()
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)

  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [filter, setFilter] = useState<CompositeFilterDescriptor>({ logic: 'and', filters: [] })
  const [paginationState, setPaginationState] = useState<Page>(getPaginationInitialState())

  const { callback, forceRefresh } = useCallbackWithForceRefresh(() => {
    if (!referralMaterialId) return Promise.resolve(undefined)
    return clientAreaApiClient.getPromoMaterialsByReferralMaterialsId(referralMaterialId, {
      pageIndex: calculatePageIndex(paginationState.skip, paginationState.take),
      pageSize: paginationState.take,
      skipCount: tableData?.skipCount,
    })
  }, [paginationState, tableData?.skipCount])
  const { data, isLoading } = useFetchOne(callback)

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!isLoading) {
        setPaginationState((page) => ({ ...page, skip: 0 }))
      }
    }, 500)
    return () => clearTimeout(timeoutId)
  }, [filter.filters])

  useEffect(() => {
    setTableData((props) => {
      return { ...props, skipCount: tableData?.skipCount, meta: data }
    })
  }, [data?.pageIndex, data?.pageSize, data?.totalResults, tableData?.skipCount, data])

  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setFilter(event.filter)
  }

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPaginationState(event.page)
  }

  return {
    pageData: data,
    data: (data?.items || []) as PromoMaterialDto[],
    tableData,
    setTableData,
    pageIndex: calculatePageIndex(paginationState.skip, paginationState.take),
    pageSize: paginationState.take ?? 10,
    isLoading,
    forceRefresh,
    filter,
    setFilter,
    pagination: paginationState,
    setPagination: setPaginationState,
    handleFilterChange,
    handlePageChange,
  }
}

const useTablePager = (props: ReturnType<typeof useRelatedPromoMaterialsFilter>) => {
  const { pageData, pagination, setPagination, tableData, setTableData } = props

  useEffect(() => {
    setTableData((state) => {
      return { ...state, skipCount: tableData?.skipCount, meta: pageData }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageData?.pageIndex, pageData?.pageSize, pageData?.totalResults, tableData?.skipCount])

  const handlePageChangePager = (pagerIndex: number) => {
    setPagination((state) => {
      return { ...state, skip: calculateSkipIndex(pagerIndex, pagination.take) }
    })
  }

  const handleSizeChangePager = (take: number) => {
    setPagination((state) => {
      return { ...state, skip: 0, take }
    })
  }

  const handleSkipCountChangePager = (skipCount: boolean) => {
    setTableData((state) => {
      return { ...state, skipCount }
    })
  }

  return { handlePageChangePager, handleSizeChangePager, handleSkipCountChangePager }
}

export interface FileInfo extends UploadFileInfo {
  languageId: string
  contentType: NameDto
  fileSize: NameDto
  enabled: boolean
  endDateTime: string | null
  promoAsset: string
  inEdit: boolean
}

const usePromoMaterialMutate = (tickmillCompanyId?: number) => {
  const { referralMaterialId } = useReferralMaterialRoute()

  const clientAreaApiClient = useApiClient(ClientAreaApiClient)
  const setToast = useContext(ToastContext)

  const addOne = useCallback(
    async (data: CreatePromoMaterial, callback: () => void) => {
      if (referralMaterialId && tickmillCompanyId) {
        try {
          await clientAreaApiClient.createPromoMaterialsForReferralMaterial({
            referralMaterialId,
            promoAssets: [data],
          })
          setToast(successToast('Promo Material added successfully.'))
          callback()
        } catch (error) {
          if (error instanceof ResponseError && error?.response?.message) {
            setToast(errorToast(error.response.message))
          }
        }
      }
    },
    [clientAreaApiClient, referralMaterialId, setToast, tickmillCompanyId]
  )

  const updateOne = useCallback(
    async (data: UpdatePromoMaterialValues, callback: () => void) => {
      if (referralMaterialId && tickmillCompanyId) {
        await clientAreaApiClient.updatePromoMaterialsByPromoMaterialId(
          referralMaterialId,
          data.id,
          data
        )
        setToast(successToast('Promo Material update successfully.'))
        callback()
      }
    },
    [clientAreaApiClient, referralMaterialId, setToast, tickmillCompanyId]
  )

  const addBulk = useCallback(
    async (data: FileInfo[], callback: () => void) => {
      if (referralMaterialId && tickmillCompanyId) {
        try {
          const promoAssets: CreatePromoMaterial[] = data
            .filter((x): x is FileInfo => !!x?.name && !!x?.languageId)
            .map((x) => {
              return {
                name: x.name,
                languageId: x.languageId,
                enabled: x.enabled,
                endDateTime: x.endDateTime,
                promoAsset: x.promoAsset,
              } as CreatePromoMaterial
            })
          await clientAreaApiClient.createPromoMaterialsForReferralMaterial({
            referralMaterialId,
            promoAssets,
          })
          setToast(successToast('Promo Materials added successfully.'))
          callback()
        } catch (error) {
          if (error instanceof ResponseError && error?.response?.message) {
            setToast(errorToast(error.response.message))
          }
        }
      }
    },
    [clientAreaApiClient, referralMaterialId, setToast, tickmillCompanyId]
  )

  const validate = useCallback(
    async (data: CreatePromoMaterial[]) => {
      if (referralMaterialId && tickmillCompanyId) {
        try {
          const result = await clientAreaApiClient.validatePromoMaterialsForReferralMaterial({
            referralMaterialId,
            promoAssets: data,
          })
          if (result.errors) {
            const errors = result.promoAssets.map((x) => x.error?.name).filter((name) => name)
            const [error] = errors
            if (error) {
              setToast(errorToast(error))
              throw new Error(error)
            }
          } else {
            setToast(successToast('Promo Material validated successfully.'))
            return result
          }
        } catch (error) {
          if (error instanceof ResponseError) {
            if (error?.response?.message) {
              setToast(errorToast(error.response.message))
              throw new Error(error.response.message)
            }
          }

          if (error instanceof Error) {
            setToast(errorToast(error.message))
            throw new Error(error.message)
          }
        }
      }
    },
    [clientAreaApiClient, referralMaterialId, setToast, tickmillCompanyId]
  )

  const remove = useCallback(
    async (data?: PromoMaterialDto) => {
      if (referralMaterialId && data?.id) {
        try {
          await clientAreaApiClient.deletePromoMaterialsByPromoMaterialId(
            referralMaterialId,
            data.id
          )
          setToast(successToast('Promo Material removed successfully.'))
        } catch (error) {
          if (error instanceof ResponseError && error?.response?.message) {
            setToast(errorToast(error.response.message))
          }
        }
      }
    },
    [clientAreaApiClient, referralMaterialId, setToast]
  )

  return { addOne, addBulk, updateOne, validate, remove }
}

interface ModalProps {
  data: PromoMaterialDto | undefined
  visible: boolean
}

const initialModalState = { data: undefined, visible: false }

const useConfirmationModal = () => {
  const [confirmationModal, setConfirmationModal] = useState<ModalProps>(initialModalState)

  const handleConfirmationModalOpen = useCallback((data?: PromoMaterialDto) => {
    if (data) {
      setConfirmationModal({ data, visible: true })
    }
  }, [])

  const handleConfirmationModalClose = useCallback(() => {
    setConfirmationModal(initialModalState)
  }, [])

  return { confirmationModal, handleConfirmationModalOpen, handleConfirmationModalClose }
}

const useReferralMaterialRoute = () => {
  const params = useParams<{ id: string }>()
  const referralMaterialId = params.id

  return { referralMaterialId }
}

interface OuterProps {
  caLanguages: NameDto<string>[]
  sizes: NameDto[]
  tickmillCompanyId?: number
  updateReferralMaterial: () => void
}

interface ModalProps {
  data: PromoMaterialDto | undefined
  visible: boolean
}

export const RelatedPromoMaterialTable: React.FC<OuterProps> = (props) => {
  const { caLanguages, sizes, tickmillCompanyId, updateReferralMaterial } = props
  const { referralMaterialId } = useReferralMaterialRoute()

  const [isManualUploadModalOpen, setManualUploadModalOpen] = useState<boolean>(false)
  const [editModalOpen, setEditModal] = useState<ModalProps>(initialModalState)
  const [isBulkModalOpen, setBulkModalOpen] = useState<boolean>(false)
  const [promoMaterialsPreview, setPromoMaterialsPreview] = useState<PreviewPromoMaterial[]>([])
  const [auditLogData, setAuditLogData] = useState<PromoMaterialDto | null>(null)
  const [auditLogVisible, setAuditLogVisible] = useState(false)
  const { confirmationModal, handleConfirmationModalOpen, handleConfirmationModalClose } =
    useConfirmationModal()

  const relatedPromoMaterialsFilter = useRelatedPromoMaterialsFilter()
  const tablePager = useTablePager(relatedPromoMaterialsFilter)

  const { isLoading } = relatedPromoMaterialsFilter
  const { forceRefresh } = relatedPromoMaterialsFilter

  const { addOne, addBulk, updateOne, validate, remove } = usePromoMaterialMutate(tickmillCompanyId)

  const handleAddUploadModalOpen = useCallback(() => {
    setManualUploadModalOpen(true)
  }, [])

  const handleBulkUploadModalOpen = useCallback(() => {
    setBulkModalOpen(true)
  }, [])

  const handleEditModalOpen = useCallback((values: PromoMaterialDto) => {
    setEditModal({ data: values, visible: true })
  }, [])

  const handleManualUpload = useCallback(
    async (values: CreatePromoMaterial) => {
      await addOne(values, () => {
        setManualUploadModalOpen(false)
        forceRefresh()
      })
    },
    [addOne, forceRefresh]
  )

  const handleUploadValidation = useCallback(
    async (values: CreatePromoMaterial[]): Promise<PreviewPromoMaterial[] | undefined> => {
      try {
        const result = await validate(values)
        if (result?.promoAssets) {
          setPromoMaterialsPreview(result.promoAssets)
          return result.promoAssets || []
        }
      } catch (error) {
        if (error instanceof ResponseError) {
          if (error?.response?.message) {
            throw new Error(error.response.message)
          }
        }

        if (error instanceof Error) {
          throw new Error(error.message)
        }
      }
    },
    [validate]
  )

  const handleManualEdit = useCallback(
    async (values: UpdatePromoMaterialValues, shouldUpdateReferralMaterial?: boolean) => {
      await updateOne(values, () => {
        setEditModal(initialModalState)
        forceRefresh()
      })

      if (shouldUpdateReferralMaterial) {
        updateReferralMaterial()
      }
    },
    [forceRefresh, updateOne, updateReferralMaterial]
  )

  const handleBulkUpload = useCallback(
    async (values: FileInfo[]) => {
      await addBulk(values, () => {
        setBulkModalOpen(false)
        forceRefresh()
      })
    },
    [addBulk, forceRefresh]
  )

  const handleRemovePromoMaterial = useCallback(
    async (promoMaterial?: PromoMaterialDto) => {
      if (promoMaterial?.id) {
        await remove(promoMaterial)
        forceRefresh()
        handleConfirmationModalClose()
      }
    },
    [forceRefresh, handleConfirmationModalClose, remove]
  )

  const handleAuditLogOpen = useCallback((promoMaterial: PromoMaterialDto) => {
    setAuditLogData(promoMaterial)
    setAuditLogVisible(true)
  }, [])

  return (
    <React.Fragment>
      {auditLogVisible && auditLogData && (
        <ClientAreaAuditLogModal
          isVisible={auditLogVisible}
          onClose={() => setAuditLogVisible(false)}
          referralMaterialId={referralMaterialId}
          promoMaterialId={auditLogData?.id || ''}
        />
      )}
      {confirmationModal.data && (
        <ConfirmationModal
          yesButtonText='Proceed'
          onYes={() => handleRemovePromoMaterial(confirmationModal.data)}
          onCancel={handleConfirmationModalClose}
          visible={confirmationModal.visible}
          renderTitle={
            <div>
              <p>Are you sure you want to delete this Promo Material?</p>
            </div>
          }
        />
      )}
      {isManualUploadModalOpen && (
        <Dialog open={isManualUploadModalOpen} onOpenChange={() => setManualUploadModalOpen(false)}>
          <PromoMaterialSaveForm
            caLanguages={caLanguages}
            sizes={sizes}
            promoMaterialsPreview={promoMaterialsPreview}
            onSubmit={handleManualUpload}
            onValidate={handleUploadValidation}
          />
        </Dialog>
      )}
      {editModalOpen.visible && editModalOpen.data && (
        <Dialog open={editModalOpen.visible} onOpenChange={() => setEditModal(initialModalState)}>
          <PromoMaterialEditForm
            caLanguages={caLanguages}
            referralMaterialId={referralMaterialId}
            promoMaterial={editModalOpen.data}
            sizes={sizes}
            onSubmit={handleManualEdit}
          />
        </Dialog>
      )}
      {isBulkModalOpen && caLanguages && (
        <Dialog open={isBulkModalOpen} onOpenChange={() => setBulkModalOpen(false)}>
          <RelatedPromoMaterialUploadTable
            caLanguages={caLanguages}
            promoMaterialsPreview={promoMaterialsPreview}
            sizes={sizes}
            onSubmit={handleBulkUpload}
            onPromoMaterialValidate={handleUploadValidation}
          />
        </Dialog>
      )}
      <div className='px-4 md:px-6 pb-4'>
        <DataCard title='Promo Asset(s)'>
          <div className='flex flex-wrap items-center gap-2'>
            <Button
              type='button'
              disabled={isLoading}
              onClick={handleBulkUploadModalOpen}
              data-test='save-button'
            >
              Bulk Upload
            </Button>
            <AddButton
              text='Manual Upload'
              disabled={isLoading}
              onClick={handleAddUploadModalOpen}
            />
          </div>
          <Loading isLoading={isLoading}>
            <RelatedPromoMaterialGrid
              {...props}
              tablePager={tablePager}
              relatedPromoMaterialsFilter={relatedPromoMaterialsFilter}
              onEdit={handleEditModalOpen}
              onDelete={handleConfirmationModalOpen}
              onAuditLogOpen={handleAuditLogOpen}
              setAuditLogModalOpen={setAuditLogVisible}
              onStatusUpdate={handleManualEdit}
              caLanguages={caLanguages}
            />
          </Loading>
        </DataCard>
      </div>
    </React.Fragment>
  )
}

interface RelatedPromoMaterialGridProps {
  relatedPromoMaterialsFilter: ReturnType<typeof useRelatedPromoMaterialsFilter>
  tablePager: ReturnType<typeof useTablePager>
  onEdit(data: PromoMaterialDto): void
  onDelete(data: PromoMaterialDto): void
  onAuditLogOpen(data: PromoMaterialDto): void
  setAuditLogModalOpen(value: boolean): void
  onStatusUpdate: (data: UpdatePromoMaterialValues, shouldUpdateReferralMaterial?: boolean) => void
  caLanguages: NameDto<string>[]
}

const RelatedPromoMaterialGrid: React.FC<RelatedPromoMaterialGridProps> = (props) => {
  const {
    relatedPromoMaterialsFilter,
    tablePager,
    onEdit,
    onDelete,
    onAuditLogOpen,
    onStatusUpdate,
    caLanguages,
  } = props

  const {
    data: promoMaterials,
    pageData,
    filter,
    pagination,
    pageSize,
    pageIndex,
  } = relatedPromoMaterialsFilter
  const { handlePageChange, handleFilterChange } = relatedPromoMaterialsFilter
  const { tableData, setTableData } = relatedPromoMaterialsFilter
  const { handlePageChangePager, handleSizeChangePager, handleSkipCountChangePager } = tablePager

  const setToast = useContext(ToastContext)

  const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState<boolean>(false)
  const [preservedDataItem, setPreservedDataItem] = useState<UpdatePromoMaterialValues | null>(null)

  const handleStatusUpdate = useCallback(
    (dataItem: PromoMaterialDto) => {
      const mappedDataItem = {
        name: dataItem.name,
        languageId: dataItem.language.id,
        enabled: !dataItem.enabled,
        endDateTime: dataItem.endDateTime,
        promoAsset: dataItem.imagePath || '',
        id: dataItem.id,
      } as UpdatePromoMaterialValues

      const shouldAddLanguageIfMissing =
        !dataItem.enabled &&
        caLanguages.findIndex((language) => language.id === dataItem.language.id) === -1

      if (shouldAddLanguageIfMissing) {
        mappedDataItem.addLanguageIfMissing = true
        setPreservedDataItem(mappedDataItem)
        setIsConfirmationModalVisible(true)
        return
      }

      onStatusUpdate(mappedDataItem)
    },
    [caLanguages, onStatusUpdate]
  )

  const handleConfirmationModalConfirm = () => {
    if (preservedDataItem) {
      onStatusUpdate(preservedDataItem, true)
    }

    setIsConfirmationModalVisible(false)
  }

  const handleConfirmationModalCancel = () => {
    setPreservedDataItem(null)
    setToast(errorToast('Promo asset is not Activated'))
    setIsConfirmationModalVisible(false)
  }

  const columns = useMemo(() => promoMaterialColumn(onEdit).filter(filterColumn), [onEdit])

  const actionCell = useCallback(
    (gridCellProps: GridCellProps) => (
      <td>
        <MoreButton
          items={[
            {
              name: 'Delete',
              onClick: () => onDelete(gridCellProps.dataItem),
            },
            {
              name: 'Audit Log',
              onClick: () => onAuditLogOpen(gridCellProps.dataItem),
            },
            {
              name: gridCellProps.dataItem.enabled ? 'Deactivate' : 'Activate',
              onClick: () => handleStatusUpdate(gridCellProps.dataItem),
            },
          ]}
        />
      </td>
    ),
    [handleStatusUpdate, onAuditLogOpen, onDelete]
  )

  return (
    <TableStateContext.Provider value={[tableData, setTableData]}>
      <Grid
        resizable
        navigatable={false}
        dataItemKey='id'
        data={promoMaterials || []}
        filter={filter}
        take={pagination.take}
        skip={pagination.skip}
        total={pageData?.totalResults}
        onPageChange={handlePageChange}
        onFilterChange={handleFilterChange}
        pageable={pageableSettings}
        pager={() => (
          <Pager
            namespace='related-promo-material-table'
            onSkipCountChange={handleSkipCountChangePager}
            onPageChange={handlePageChangePager}
            onPageSizeChange={handleSizeChangePager}
            page={pageIndex}
            pageSize={pageSize}
            take={pagination.take || 10}
            skip={pagination.skip || 0}
          />
        )}
      >
        {columns.map((column, idx) => (
          <Column key={idx + 2} {...column} />
        ))}
        <Column title='' width={50} cell={actionCell} />
      </Grid>
      <ConfirmationModal
        contentText='Do you want to add related language to Referral Material?'
        yesButtonText='Yes'
        cancelButtonText='No'
        onYes={handleConfirmationModalConfirm}
        onCancel={handleConfirmationModalCancel}
        visible={isConfirmationModalVisible}
      />
    </TableStateContext.Provider>
  )
}

const filterColumn = (c: GridColumn) => {
  return c.show !== false
}
